import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";
import {
  format,
  parseISO,
  isAfter,
  isBefore,
  addHours,
  subMinutes,
} from "date-fns";
import checkIcon from "../../assets/img/check.png";
import infoIcon from "../../assets/img/info.png";
import { useTranslation } from "react-i18next";
import { createRoomCodeForAppointment } from "../../api/video-therapy/videoTherapy";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";

interface AppointmentInfoDialogProps {
  open: boolean;
  onClose: () => void;
  appointment: any | null;
  loading?: boolean;
  onCancelAppointment?: (_id: string) => void;
}

const AppointmentInfoDialog: React.FC<AppointmentInfoDialogProps> = ({
  open,
  onClose,
  appointment,
  onCancelAppointment,
  loading,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isJoining, setIsJoining] = useState(false);

  if (!appointment) return null;

  const startDate = appointment.startTime
    ? parseISO(appointment.startTime)
    : null;
  const endDate = appointment.endTime ? parseISO(appointment.endTime) : null;
  const now = new Date();

  const canJoinRoom =
    appointment.roomId &&
    startDate &&
    isAfter(now, subMinutes(startDate, 15)) &&
    isBefore(now, addHours(startDate, 1));

  const handleJoinRoom = async () => {
    if (!appointment?.id) return;
    setIsJoining(true);
    try {
      const res = await createRoomCodeForAppointment(appointment.id);
      const roomCode = res?.data?.roomCode;
      if (roomCode) {
        navigate(`/join-form?roomCode=${roomCode}`);
      } else {
        toast.error(t("title.roomCodeNotFound"));
      }
    } catch (error) {
      toast.error(t("title.failedToCreateRoomCode"));
    } finally {
      setIsJoining(false);
    }
  };

  const handleCancelClick = () => {
    if (appointment?.id && onCancelAppointment) {
      onCancelAppointment(appointment.id);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: 8,
          maxWidth: 550,
          maxHeight: "calc(100vh - 100px)",
          width: { xs: "calc(100% - 32px)", sm: "100%" },
          margin: "0 auto",
        },
      }}
    >
      <DialogContent
        sx={{ pt: 5, pb: 4, px: { xs: 3, sm: 4 }, overflow: "hidden" }}
      >
        {/* Icon section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 3,
          }}
        >
          <Box
            sx={{
              width: { xs: 60, sm: 70 },
              height: { xs: 60, sm: 70 },
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor:
                appointment.status === "approved" ? "#e0f8e8" : "#FFF8E8",
            }}
          >
            <Box
              component="img"
              src={appointment.status === "approved" ? checkIcon : infoIcon}
              alt={appointment.status === "approved" ? "Approved" : "Pending"}
              sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 } }}
            />
          </Box>
        </Box>

        {/* Title */}
        <Typography
          variant="h6"
          align="center"
          sx={{
            fontWeight: 600,
            fontSize: { xs: "1.2rem", sm: "1.4rem" },
            mb: 1,
            color: "#333",
          }}
        >
          {t("appointment.information")}
        </Typography>
        {/* Status message */}
        <Typography
          variant="body2"
          align="center"
          sx={{
            mb: 4,
            fontSize: { xs: "0.875rem", sm: "1rem" },
            fontWeight:
              appointment.status === "approved" && canJoinRoom ? 500 : 400,
          }}
        >
          {appointment.status === "approved"
            ? canJoinRoom
              ? t("appointment.youCanNowEnterTheRoom")
              : t("appointment.isNotPresent")
            : t("appointment.waitingForConfirmation")}
        </Typography>

        {/* Appointment card */}
        <Box
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            mb: 4,
            width: "100%",
            maxWidth: { xs: "100%", sm: "90%" },
            mx: "auto",
            backgroundColor: "#f5f9ff",
            p: { xs: 3, sm: 4 },
            textAlign: "center",
            border: "1px solid #1976d2",
            boxShadow: "0 0 0 1px rgba(25, 118, 210, 0.1)",
          }}
        >
          {/* Appointment title */}
          <Typography
            variant="h6"
            align="center"
            sx={{
              fontSize: { xs: "1rem", sm: "1.1rem" },
              fontWeight: 600,
              color: "#1976d2",
              mb: 2,
            }}
          >
            {appointment.title || "Individual Therapy"}
          </Typography>

          {/* Appointment description */}
          {appointment.description && (
            <Typography
              variant="body2"
              sx={{
                color: "#3178c6",
                mb: 2,
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              {appointment.description}
            </Typography>
          )}

          {/* Date and time */}
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: "#3178c6",
                mb: 0.5,
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              {startDate ? format(startDate, "EEEE, MMMM d, yyyy") : ""}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "#3178c6",
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              {startDate ? format(startDate, "h:mm a") : ""} -{" "}
              {endDate ? format(endDate, "h:mm a") : ""}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          p: { xs: 2, sm: 3 },
          pb: { xs: 3, sm: 4 },
          px: { xs: 3, sm: 4 },
          display: "flex",
          justifyContent: "space-between",
          maxWidth: { xs: "100%", sm: "90%" },
          mx: "auto",
          width: "100%",
          gap: { xs: 2, sm: 3 },
        }}
      >
        {/* Close button - always visible */}
        <Button
          variant="outlined"
          onClick={onClose}
          fullWidth
          sx={{
            borderRadius: 2,
            py: { xs: 1.5, sm: 1.75 },
            backgroundColor: "#FFF8F0",
            color: "#E67C52",
            textTransform: "none",
            border: "none",
            fontWeight: "bold",
            fontSize: { xs: "0.875rem", sm: "1rem" },
            flex: 1,
            "&:hover": {
              backgroundColor: "#FFE8E0",
              border: "none",
            },
          }}
        >
          {t("appointment.close")}
        </Button>

        {/* Conditional buttons based on status */}
        {appointment.status === "approved" && (
          <Button
            variant="contained"
            onClick={handleJoinRoom}
            disabled={!canJoinRoom || isJoining}
            fullWidth
            sx={{
              borderRadius: 2,
              py: { xs: 1.5, sm: 1.75 },
              backgroundColor: "#E67C52",
              fontWeight: "bold",
              textTransform: "none",
              fontSize: { xs: "0.875rem", sm: "1rem" },
              flex: 1,
              "&:hover": {
                backgroundColor: "#D95C2F",
              },
            }}
          >
            {isJoining ? (
              <span className="flex items-center justify-center">
                <CircularProgress size={20} sx={{ color: "#fff", mr: 1 }} />
                {t("appointment.joinRoom")}
              </span>
            ) : (
              t("appointment.joinRoom")
            )}
          </Button>
        )}

        {appointment.status === "pending" && (
          <Button
            variant="contained"
            onClick={handleCancelClick}
            disabled={loading}
            fullWidth
            sx={{
              borderRadius: 2,
              py: { xs: 1.5, sm: 1.75 },
              backgroundColor: "#E74C3C",
              fontWeight: "bold",
              textTransform: "none",
              fontSize: { xs: "0.875rem", sm: "1rem" },
              flex: 1,
              "&:hover": {
                backgroundColor: "#C0392B",
              },
            }}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <CircularProgress size={20} sx={{ color: "#fff", mr: 1 }} />
                {t("appointment.cancelling")}
              </span>
            ) : (
              t("appointment.cancel")
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AppointmentInfoDialog;
